import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Constants from "../Utilities/Constants";
import Product from "../Components/Product";
import SideBarGallery from "../Components/SideBar";
import Banners from "../Components/Banners";
import Breadcrumb from "../Components/Breadcrumb";
import LoadingScreen from "../Components/LoadingScreen";
import SortProducts from "../Components/SortProducts";
import { useAppContext } from "../Contexts/AppContext";
import { getSortedProducts } from "../Utilities/sortProducts";
import { usePageTitle } from "../Utilities/usePageTitle";


const MY_PRODUCTS_KEY = 'MyProducts';
const PAGE_SIZE = 50;

const Category = () => {
    const [products, setProducts] = useState([]); // Enriched data with units & labels
    const { category, subcategory } = useParams();
    const [title, setTitle] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [sortType, setSortType] = useState('default');
    const { customer, setShowLoginModal } = useAppContext();
    usePageTitle(title);

    // Infinite scroll - category/subcategory browsing can be thousands of products, so only
    // the first 50 load up front; more load in as the user scrolls near the bottom.
    // Not used for isMyProducts (a customer's own product list is never large enough to matter).
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const loadingMoreRef = useRef(false); // mirrors loadingMore synchronously - avoids a stale
                                           // closure letting the observer fire twice before the
                                           // first fetch's state update has been applied

    const isMyProducts = category === MY_PRODUCTS_KEY;

    useEffect(() => {

        const fetchTitle = async () => {

            if (category == null)
                return null;

            try {
                // API now handles the decision logic for what title to display
                var api = `${Constants.API_URL_GET_CATEGORY_TITLE}/${category}`;
                if (subcategory != null) {
                    api = `${api}/${subcategory}`;
                }

                const response = await axios.get(api);
                if (response.data && response.data.trim()) {
                    setTitle(response.data.trim());
                }
            }
            catch(error) {
                console.error("Error fetching title:", error);
                // Fallback to decoded names if API fails
                if (subcategory) {
                    setTitle(decodeURIComponent(subcategory));
                } else {
                    setTitle(decodeURIComponent(category));
                }
            }
        }

        const fetchProducts = async () => {
            if (isMyProducts) {
                setHasMore(false); // a customer's own list is never large enough to paginate
                if (!customer) {
                    setProducts([]);
                    return;
                }
                try {
                    const response = await axios.get(`${Constants.API_URL_GET_MY_PRODUCTS}/${customer.customerId}`);
                    setProducts(response.data);
                } catch(error) {
                    console.error("Error loading my products:", error);
                    setProducts([]);
                }
                return;
            }

            var api = Constants.API_URL_GET_PRODUCTS_BY_CATEGORY;

            if (category != null) { api = `${api}/${category}` }
            if (subcategory != null) { api = `${api}/${subcategory}` }

            try {
                // First page only (skip=0) - more loads in via the IntersectionObserver below
                // as the user scrolls, same pattern as the admin product list.
                const response = await axios.get(api, { params: { skip: 0 } });
                setProducts(response.data);
                setHasMore(response.data.length === PAGE_SIZE);
            }
            catch(error) {
                console.error("Error loading products:", error);
                setProducts([]);
                setHasMore(false);
            }
        }

        const loadData = async () => {
            setIsLoading(true);

            try {
                await Promise.all([
                    fetchTitle(),
                    fetchProducts()
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();

    }, [category, subcategory, customer])

    const loadMoreProducts = useCallback(async () => {
        if (isMyProducts || loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        var api = Constants.API_URL_GET_PRODUCTS_BY_CATEGORY;
        if (category != null) { api = `${api}/${category}` }
        if (subcategory != null) { api = `${api}/${subcategory}` }

        try {
            const response = await axios.get(api, { params: { skip: products.length } });
            setProducts(prev => [...prev, ...response.data]);
            setHasMore(response.data.length === PAGE_SIZE);
        } catch (error) {
            console.error("Error loading more products:", error);
            // Don't clear hasMore on a transient error - let the next scroll retry.
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [isMyProducts, category, subcategory, products.length]);

    useEffect(() => {
        if (!hasMore || !sentinelRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreProducts();
            }
        }, { rootMargin: '300px' });

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMore, loadMoreProducts]);

    const getBreadcrumbItems = () => {
        const items = [
            { label: 'דף הבית', path: '/' }
        ];
        if (category) {
            items.push({ 
                label: decodeURIComponent(category), 
                path: `/category/${category}` 
            });
        }
        if (subcategory) {
            items.push({ 
                label: decodeURIComponent(subcategory), 
                path: `/category/${category}/${subcategory}` 
            });
        }
        return items;
    };

    // if (isMyProducts && customer === undefined) {
    //     return <LoadingScreen loading={true} message="טוען..." />;
    // }

    if (isMyProducts && customer === null) {
        return (
            <div className="container py-4 text-center">
                <p>יש להתחבר כדי לצפות במוצרים שלי</p>
                <button className="btn btn-success" onClick={() => setShowLoginModal(true)}>כניסה</button>
            </div>
        );
    }

    return (
        <>
            <Banners galleryName={category} />
           
            {isLoading ?
                <LoadingScreen loading={isLoading} message="טוען מוצרים..." />
            : (
                    <div className="container py-2">
                        <div className="row">
                            <div className="col-md-2 text-center desktop">
                                <div className="d-flex flex-wrap justify-content-around w-100">
                                    <SideBarGallery />
                                </div>
                            </div>
                            <div className="col-md-10">
                                <Breadcrumb items={getBreadcrumbItems()} />
                                <div className="d-flex flex-wrap justify-content-between mb-2">
                                    <h4 className="mt-2">{title}</h4>
                                    <SortProducts onSortChange={setSortType} currentSort={sortType} />
                                </div>
                                <hr/>
                                <div className="d-flex flex-wrap justify-content-around">
                                    {products.length > 0 ? (
                                        getSortedProducts(products, sortType).map((product, key) => (
                                            <Product 
                                                product={product} // Already enriched with units & labels
                                                key={key} 
                                                category={category}
                                                subcategory={subcategory}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-center p-4">
                                            <p>לא נמצאו מוצרים בקטגוריה זו</p>
                                        </div>
                                    )}
                                </div>
                                {hasMore && <div ref={sentinelRef} />}
                                {loadingMore && (
                                    <div className="text-center text-muted py-2">טוען מוצרים נוספים...</div>
                                )}
                            </div>
                        </div>
                    </div>

            )}
        </>
    );
}

export default Category;